import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { courseService, Course, Lesson } from '../../api/courseService';
import { theme } from '../../styles/theme';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { AxiosResponse } from 'axios';

const CourseDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const CourseHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const CourseBanner = styled.div`
  height: 200px;
  border-radius: ${theme.radii.medium};
  overflow: hidden;
  margin-bottom: ${theme.spacing.md};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CourseInfo = styled.div`
  h1 {
    font-size: ${theme.fontSizes.xxlarge};
    margin-bottom: ${theme.spacing.sm};
  }
  
  p {
    font-size: ${theme.fontSizes.medium};
    color: ${theme.colors.gray};
  }
`;

const EnrollmentStatus = styled.div`
  display: inline-block;
  background-color: ${theme.colors.light};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.radii.small};
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.fontSizes.small};
  color: ${theme.colors.primary};
`;

const CourseActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const LessonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const LessonCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  
  h3 {
    font-size: ${theme.fontSizes.large};
    margin: 0;
  }
  
  p {
    font-size: ${theme.fontSizes.medium};
    color: ${theme.colors.gray};
    margin-bottom: ${theme.spacing.sm};
  }
`;

const NotEnrolledMessage = styled.div`
  background-color: ${theme.colors.light};
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.medium};
  margin-bottom: ${theme.spacing.md};
  
  h3 {
    margin-top: 0;
    margin-bottom: ${theme.spacing.sm};
  }
  
  p {
    margin-bottom: ${theme.spacing.md};
  }
`;

const LoadingContainer = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.gray};
`;

const ErrorContainer = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.danger};
  text-align: center;
`;

const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [manualIsEnrolled, setManualIsEnrolled] = React.useState<boolean | null>(null);
  
  // Get enrolled courses to double-check enrollment status
  const { data: enrolledCoursesData } = useQuery({
    queryKey: ['enrolledCourses'],
    queryFn: () => courseService.getEnrolledCourses(),
  });
  
  // Check authentication status
  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('CourseDetail - Auth token present:', !!token);
  }, []);
  
  // Check enrollment status manually from enrolledCourses
  useEffect(() => {
    if (enrolledCoursesData?.data?.data && courseId) {
      const enrolledCourses = enrolledCoursesData.data.data;
      const isEnrolled = enrolledCourses.some(
        enrollment => String(enrollment.course_id) === courseId
      );
      console.log('Manual enrollment check:', isEnrolled, 'for courseId:', courseId);
      setManualIsEnrolled(isEnrolled);
    }
  }, [enrolledCoursesData, courseId]);
  
  const { data: courseData, isLoading: courseLoading, error: courseError } = useQuery<AxiosResponse<{success: boolean, data: Course}>, Error>({
    queryKey: ['course', courseId],
    queryFn: () => courseService.getCourseById(courseId || ''),
    enabled: !!courseId,
  });
  
  const { data: lessonsData, isLoading: lessonsLoading, error: lessonsError } = useQuery<AxiosResponse<{success: boolean, data: Lesson[]}>, Error>({
    queryKey: ['lessons', courseId],
    queryFn: () => courseService.getLessons(courseId || ''),
    enabled: !!courseId && !courseData?.data?.data?.lessons,
  });

  // Enrollment mutation
  const enrollMutation = useMutation({
    mutationFn: (id: string) => courseService.enrollInCourse(id),
    onSuccess: async () => {
      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ['course', courseId] });
      await queryClient.invalidateQueries({ queryKey: ['availableCourses'] });
      await queryClient.invalidateQueries({ queryKey: ['enrolledCourses'] });
      
      // Force refetch the course data
      await queryClient.refetchQueries({ queryKey: ['course', courseId] });
    },
  });

  // Unenrollment mutation
  const unenrollMutation = useMutation({
    mutationFn: (id: string) => courseService.unenrollFromCourse(id),
    onSuccess: async () => {
      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ['course', courseId] });
      await queryClient.invalidateQueries({ queryKey: ['availableCourses'] });
      await queryClient.invalidateQueries({ queryKey: ['enrolledCourses'] });
      
      // Navigate back to courses list
      navigate('/courses');
    },
  });

  // Handle enrollment
  const handleEnroll = () => {
    if (courseId) {
      enrollMutation.mutate(courseId);
    }
  };

  // Handle unenrollment
  const handleUnenroll = () => {
    if (courseId && window.confirm('Are you sure you want to unenroll from this course? Your progress will be saved, but you will need to enroll again to access the content.')) {
      unenrollMutation.mutate(courseId);
    }
  };

  if (courseLoading || lessonsLoading) {
    return (
      <CourseDetailContainer>
        <LoadingContainer>Loading course details...</LoadingContainer>
      </CourseDetailContainer>
    );
  }

  if (courseError || lessonsError) {
    console.error('Course error:', courseError || lessonsError);
    return (
      <CourseDetailContainer>
        <ErrorContainer>Unable to load course details. Please try again later.</ErrorContainer>
        <Button as={Link} to="/courses">Back to Courses</Button>
      </CourseDetailContainer>
    );
  }

  // Get course data from API response
  const course = courseData?.data?.data;
  
  if (!course) {
    return (
      <CourseDetailContainer>
        <ErrorContainer>Course not found</ErrorContainer>
        <Button as={Link} to="/courses">Back to Courses</Button>
      </CourseDetailContainer>
    );
  }
  
  // Get lessons either from course data or from separate lessons query
  const lessons = course.lessons || lessonsData?.data?.data || [];
  
  console.log('Course detail - lessons data:', lessons);
  
  // Sort lessons by order_index or order if available
  const sortedLessons = [...lessons].sort((a, b) => {
    const orderA = a.order_index ?? a.order ?? 0;
    const orderB = b.order_index ?? b.order ?? 0;
    return orderA - orderB;
  });
  
  // Check if user is enrolled - first use API status if available, fall back to manual check
  const isEnrolled = course.is_enrolled !== undefined 
    ? !!course.is_enrolled 
    : manualIsEnrolled !== null
      ? manualIsEnrolled
      : false;
  
  return (
    <CourseDetailContainer>
      <CourseHeader>
        <CourseBanner>
          <img src={course.coverImage || course.thumbnail_url || '/placeholdercourse.png'} alt={course.title} />
        </CourseBanner>
        
        <CourseInfo>
          <h1>{course.title}</h1>
          {isEnrolled && <EnrollmentStatus>Enrolled</EnrollmentStatus>}
          <p>{course.description}</p>
        </CourseInfo>
        
        <CourseActions>
          {isEnrolled ? (
            <>
              {sortedLessons.length > 0 && (
                <Button as={Link} to={`/courses/${course.id}/lessons/${sortedLessons[0].id}`}>
                  Start Course
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={handleUnenroll}
                disabled={unenrollMutation.isPending}
              >
                Unenroll
              </Button>
            </>
          ) : (
            <Button 
              onClick={handleEnroll}
              disabled={enrollMutation.isPending}
            >
              Enroll Now
            </Button>
          )}
          <Button variant="outline" as={Link} to="/courses">
            Back to Courses
          </Button>
        </CourseActions>
      </CourseHeader>
      
      <div>
        <h2>Lessons</h2>
        {!isEnrolled ? (
          <NotEnrolledMessage>
            <h3>Enrollment Required</h3>
            <p>You need to enroll in this course to access the lessons. Enrollment is free!</p>
            <Button 
              onClick={handleEnroll}
              disabled={enrollMutation.isPending}
            >
              Enroll Now
            </Button>
          </NotEnrolledMessage>
        ) : sortedLessons.length === 0 ? (
          <LoadingContainer>No lessons available for this course yet.</LoadingContainer>
        ) : (
          <LessonsList>
            {sortedLessons.map((lesson: Lesson) => (
              <LessonCard key={lesson.id}>
                <h3>{lesson.title}</h3>
                <p>{lesson.content.substring(0, 100)}...</p>
                <Button as={Link} to={`/courses/${course.id}/lessons/${lesson.id}`}>
                  Start Lesson
                </Button>
              </LessonCard>
            ))}
          </LessonsList>
        )}
      </div>
    </CourseDetailContainer>
  );
};

export default CourseDetailPage; 